/** App-wide top chrome — sits above the AppLayout grid.
 *
 * Holds the brand mark, org switcher, locale switcher, user email,
 * sign-out, and the MFA-required banner. Navigation lives in
 * AppSidebar (the left column inside AppLayout); the top bar
 * intentionally has no horizontal nav so it doesn't compete.
 */
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useLogout, useMe } from "@/lib/auth";
import { usePartnerMe } from "@/lib/partner";

import { OrgSwitcher } from "@/modules/dashboard";

import { LocaleSwitcher } from "./LocaleSwitcher";
import { MfaRequiredBanner } from "./MfaRequiredBanner";


export function TopBar() {
  const me = useMe();
  const partner = usePartnerMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-40 border-b border-nb-border bg-nb-bg/80 backdrop-blur">
      <MfaRequiredBanner />
      <div className="mx-auto flex max-w-screen-2xl 2xl:max-w-[105rem] items-center justify-between px-6 py-3">
        <div className="flex items-center gap-6">
          <Link to="/dashboard" className="font-semibold tracking-tight text-nb-text">
            Nex<span className="text-nb-mint">.</span>Basira<span className="text-nb-mint">/</span>
          </Link>
          <OrgSwitcher />
          {partner.data && (
            <Link
              to="/partner"
              className="hidden rounded-md border border-nb-mint/40 px-2.5 py-1 text-xs font-medium text-nb-mint transition hover:bg-nb-mint/10 sm:inline-block"
            >
              ← {t("partner.consoleLink")}
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-nb-text">
          <SearchHint />
          {/* Backlog 2026-09-22 — this was a bare muted link with only a
              tooltip, so it read as a LABEL rather than a control and
              people never discovered their profile was behind it. Given
              the same bordered-pill treatment as Sign out next to it,
              plus a person icon, so it's visibly a button.
              
              2026-09-23 — the email came out. With the label added
              alongside it the pill grew wide enough to wrap onto two
              lines, and an address is a poor button label anyway. The
              "which identity am I acting as" signal it carried is not
              lost: it stays in the tooltip and the accessible name, and
              the case that actually matters — a super-admin
              impersonating a partner — has its own persistent
              `ImpersonationBanner`. */}
          {me.data && (
            <Link
              to="/account"
              className="hidden items-center gap-1.5 rounded-md border border-nb-border px-3 py-1.5 text-sm font-medium text-nb-text-2 transition hover:border-nb-mint/60 hover:bg-nb-mint/10 hover:text-nb-mint sm:inline-flex"
              title={t("nav.myProfileOf", {
                defaultValue: "My profile ({{email}})",
                email: me.data.user.email,
              })}
              aria-label={t("nav.myProfileOf", {
                defaultValue: "My profile ({{email}})",
                email: me.data.user.email,
              })}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="7"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              <span>{t("nav.myProfile", "My profile")}</span>
            </Link>
          )}
          <LocaleSwitcher />
          <button
            className="inline-flex items-center gap-1.5 rounded-md border border-nb-border px-3 py-1.5 text-sm font-medium text-nb-text-2 transition hover:border-nb-danger/60 hover:bg-nb-danger/10 hover:text-nb-danger"
            onClick={() => {
              logout.mutate(undefined, {
                onSettled: () => navigate("/login", { replace: true }),
              });
            }}
            title={t("auth.signOut")}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{t("auth.signOut")}</span>
          </button>
        </div>
      </div>
    </header>
  );
}


/** Inline "Search · ⌘K" affordance so operators discover the
 * Cmd/Ctrl+K palette without reading the changelog. Dispatches the
 * same keyboard event GlobalSearchPaletteHost listens for. */
function SearchHint() {
  const { t } = useTranslation();
  const onClick = () => {
    const isMac =
      typeof navigator !== "undefined" &&
      navigator.userAgent.toLowerCase().includes("mac");
    const ev = new KeyboardEvent("keydown", {
      key: "k",
      metaKey: isMac,
      ctrlKey: !isMac,
      bubbles: true,
    });
    window.dispatchEvent(ev);
  };
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.userAgent.toLowerCase().includes("mac");
  return (
    <button
      type="button"
      onClick={onClick}
      className="hidden items-center gap-2 rounded-md border border-nb-border bg-nb-surface/40 px-2.5 py-1 text-xs text-nb-text-2 transition hover:border-nb-mint hover:text-nb-text sm:flex"
      aria-label={t("common.search")}
      data-testid="search-hint"
    >
      <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path
          d="M20 20l-3-3"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <span>{t("common.search")}</span>
      <kbd className="rounded border border-nb-border bg-nb-bg/60 px-1.5 py-0.5 text-[10px]">
        {isMac ? "⌘K" : "Ctrl+K"}
      </kbd>
    </button>
  );
}
